import { Drawer, DrawerProps, Typography, Box } from '@mui/material';
import React, { CSSProperties, FC, useRef } from 'react';
import { Back, Close } from '@menuzen/icon/build/outline';
import useSwipe from '../../hooks/useSwipe';

export interface DialogProps extends DrawerProps {
    title?: string;
    subtitle?: string;
    onBack?: () => void;
    onClose: (e: any) => void;
    showClose?: boolean;
    hideBackdrop?: boolean;
    childClassName?: string;
    headerClassName?: string;
    closeButtonClassName?: string;
    headerStyle?: CSSProperties;
    showBarIcon?: boolean;
    borderGradient?: string;
}

const Dialog: FC<DialogProps> = ({
    title,
    subtitle,
    showClose,
    onBack,
    onClose,
    children,
    hideBackdrop,
    sx,
    headerClassName = 'flex w-full px-28 mt-12 relative h-28 shrink-0',
    closeButtonClassName = 'flex-shrink-0 text-24 text-primary-70 absolute right-28 top-0 cursor-pointer z-10',
    headerStyle,
    childClassName,
    showBarIcon = true,
    borderGradient,
    open,
    ...props
}) => {
    const paperRef: React.RefObject<HTMLDivElement> = useRef(null);

    const { transform, onTouchStart, onTouchMove, onTouchEnd } = useSwipe({
        open: !!open,
        onClose: () => onClose(1),
        paperRef,
    });

    return (
        <Drawer
            {...props}
            open={!!open}
            PaperProps={{
                style: {
                    maxWidth: 620,
                    margin: '0px auto',
                    paddingTop: 0,
                    borderBottomLeftRadius: '0px',
                    borderBottomRightRadius: '0px',
                    transform,
                },
                ref: paperRef,
            }}
            onClose={onClose}
            anchor="bottom"
            sx={!hideBackdrop ? { ...sx } : { width: 0, '& .MuiBackdrop-root': { width: 0 }, ...sx }}
        >
            <div className={borderGradient ? `rounded-t-16 ${borderGradient}` : ''}>
                <div className={borderGradient ? 'rounded-t-16 bg-white overflow-hidden' : ''}>
                    <div
                        className="pt-16"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <div className={`flex justify-center text-primary-40 text-6`}>
                            {showBarIcon && (
                                <svg
                                    width="50"
                                    height="5"
                                    viewBox="0 0 50 5"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect width="50" height="5" rx="2.5" fill="#E0E1E3" />
                                </svg>
                            )}
                        </div>
                        {(title || onBack || showClose) && (
                            <div className={headerClassName} style={headerStyle}>
                                {onBack && (
                                    <div
                                        className="flex-shrink-0 text-24 text-primary-70 left-28 top-0 cursor-pointer absolute z-10"
                                        onClick={onBack}
                                    >
                                        <Back />
                                    </div>
                                )}
                                <div className={`text-18 font-semibold flex-auto text-center`}>{title}</div>
                                {showClose && (
                                    <div className={closeButtonClassName} onClick={onClose}>
                                        <Close />
                                    </div>
                                )}
                            </div>
                        )}
                        {subtitle && (
                            <Typography variant="inherit" component="p" className={'text-center pb-20 text-primary-70'}>
                                {subtitle}
                            </Typography>
                        )}
                    </div>
                    <div className={childClassName || `overflow-y-auto`}>{children}</div>
                </div>
            </div>
        </Drawer>
    );
};

export default Dialog;
